import { BsdsPopover } from '@bsds-lib-component/react';
import { useEffect, useId, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

import { useCompanyStore } from '../../store/companyStore';
import {
  EMPRESA_DISPLAY_MAX_NAVBAR,
  getSelectedCompanyEmpresaDisplayLabel,
  getSelectedCompanyNomeEstipulanteApol,
  getSelectedCompanyPolicyLabel,
  hasSelectedCompanyPolicyDetails,
  hasSelectedCompanyPolicyNumber,
  isBackofficePolicySelection,
  truncateEmpresaDisplayLabel,
  type CompanyCardProps,
} from '../../types/companyType';
import Button from '../button/Button';

const SafeBsdsPopover = BsdsPopover ?? (({ children }: { children: React.ReactNode }) => <>{children}</>);

const BACKOFFICE_SESSION_LABEL = 'Backoffice';

function CompanyCard({ company }: CompanyCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const sessionRole = useCompanyStore((state) => state.sessionRole);
  const companyCardOpenSignal = useCompanyStore((state) => state.companyCardOpenSignal);
  const popoverId = useId();
  const triggerId = `company-card-trigger-${popoverId}`;

  const isBackofficeSession = String(sessionRole ?? '').trim().toUpperCase() === 'BACKOFFICE';
  const hasPolicyNumber = hasSelectedCompanyPolicyNumber(company);
  const hasPolicyDetails = hasSelectedCompanyPolicyDetails(company);
  const isBackofficePolicyWithoutDetails = Boolean(
    company && hasPolicyNumber && !hasPolicyDetails && isBackofficePolicySelection(company),
  );
  const isBackofficeSessionWithoutCompany = isBackofficeSession && !company;
  const isBackofficeSessionWithoutPolicy = isBackofficeSession && !hasPolicyNumber;

  const policyLabel = getSelectedCompanyPolicyLabel(company);
  const displayNameFull = (() => {
    if (isBackofficeSessionWithoutCompany || isBackofficeSessionWithoutPolicy) {
      return BACKOFFICE_SESSION_LABEL;
    }
    if (!company) {
      return '';
    }
    if (hasPolicyDetails) {
      return getSelectedCompanyNomeEstipulanteApol(company) || getSelectedCompanyEmpresaDisplayLabel(company);
    }
    if (hasPolicyNumber && policyLabel) {
      return policyLabel;
    }
    if (isBackofficePolicyWithoutDetails || isBackofficePolicySelection(company)) {
      return BACKOFFICE_SESSION_LABEL;
    }
    return getSelectedCompanyEmpresaDisplayLabel(company);
  })();

  const displayNameShort = truncateEmpresaDisplayLabel(displayNameFull, EMPRESA_DISPLAY_MAX_NAVBAR);
  const showCompanyDetailsPopover = Boolean(company && hasPolicyDetails);
  const showBackofficePopover = Boolean(
    isBackofficeSessionWithoutCompany
    || isBackofficeSessionWithoutPolicy
    || isBackofficePolicyWithoutDetails,
  );
  const showSelectPopover = !company && !isBackofficeSession;

  const triggerTextShort = displayNameFull ? displayNameShort : 'Selecionar';

  useEffect(() => {
    if (companyCardOpenSignal > 0) {
      setIsOpen(true);
    }
  }, [companyCardOpenSignal]);

  /** Vínculos vêm do `useCompanyStore` (única fonte). */
  const handleBackToSelection = () => {
    navigate('/company');
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        id={triggerId}
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white w-[200px] px-2 py-2 rounded-md shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-between space-x-2 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-50"
        aria-label={
          displayNameFull
            ? `Seleção atual: ${displayNameFull}`
            : 'Selecionar apólice'
        }
        title={displayNameFull && displayNameFull !== '—' ? displayNameFull : undefined}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls={popoverId}
      >
        <div className="flex items-center space-x-2">
          <span
            className={`text-sm font-medium truncate min-w-0 ${
              displayNameFull ? 'text-primary-600' : 'text-gray-500'
            }`}
            aria-hidden
          >
            {triggerTextShort}
          </span>
        </div>
        <HiChevronDown
          className={`w-4 h-4 text-primary-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* BsdsPopover */}
      <SafeBsdsPopover
        trigger={triggerId}
        triggerAction="click"
        isOpen={isOpen}
        onBsdsPopoverDidPresent={() => setIsOpen(true)}
        onBsdsPopoverDidDismiss={() => setIsOpen(false)}
        side="bottom"
        alignment="start"
        size="cover"
        backdropDismiss
        dismissOnSelect
      >
        {/* Popover content */}
        <div
          id={popoverId}
          className="w-42 bg-white rounded-lg shadow-lg border border-gray-200 bsds-p-8"
          role="dialog"
          aria-modal="false"
          aria-labelledby={`${popoverId}-title`}
        >
          {showCompanyDetailsPopover ? (
            <>
              <div className="bsds-pb-8 bsds-mb-8 border-b border-gray-200">
                <p id={`${popoverId}-title`} className="text-xs font-medium text-gray-600 bsds-text-uppercase bsds-text-center">
                  Seleção Atual
                </p>
              </div>

              <div
                className="bsds-mb-8 bsds-p-8 rounded-md border border-primary-200"
                role="region"
                aria-label="Informações da seleção atual"
              >
                <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center">
                  <div className="bsds-flex-1">
                    <div className="text-xs font-semibold text-primary-700 bsds-mb-2 break-words" aria-label={displayNameFull}>
                      {displayNameFull}
                    </div>
                  </div>
                </div>
              </div>

              <Button
                color="primary"
                fill="outline"
                onClick={handleBackToSelection}
                children="Trocar seleção"
                className="text-sm"
                aria-label="Trocar seleção e voltar para página de seleção"
              />
            </>
          ) : showBackofficePopover ? (
            <>
              <div className="bsds-pb-8 bsds-mb-8 border-b border-gray-200">
                <p id={`${popoverId}-title`} className="text-xs font-medium text-gray-600 bsds-text-uppercase bsds-text-center">
                  {BACKOFFICE_SESSION_LABEL}
                </p>
              </div>

              <div
                className="bsds-mb-8 bsds-p-8 rounded-md border border-primary-200"
                role="region"
                aria-label="Informações do acesso backoffice"
              >
                {hasPolicyNumber && policyLabel ? (
                  <p className="text-xs font-semibold text-primary-700 bsds-mb-2" aria-label={policyLabel}>
                    {policyLabel}
                  </p>
                ) : null}
                <p className="text-xs text-gray-600">
                  {hasPolicyNumber && !hasPolicyDetails
                    ? 'Apólice selecionada sem dados do estipulante. Use a busca para carregar outra apólice.'
                    : 'Nenhuma apólice selecionada.'}
                </p>
              </div>

              <Button
                color="primary"
                fill="outline"
                onClick={handleBackToSelection}
                children={hasPolicyNumber ? 'Trocar seleção' : 'Buscar apólice'}
                className="text-sm"
                aria-label={
                  hasPolicyNumber
                    ? 'Trocar seleção e voltar para página de seleção'
                    : 'Ir para página de busca de apólice'
                }
              />
            </>
          ) : showSelectPopover ? (
            <>
              <div className="bsds-pb-8 bsds-mb-8 border-b border-gray-200">
                <p id={`${popoverId}-title`} className="text-xs text-text-color bsds-text-center">
                  Seleção
                </p>
              </div>

              <Button
                color="primary"
                fill="outline"
                onClick={handleBackToSelection}
                children="Selecionar"
                className="text-xs"
                aria-label="Ir para página de seleção"
              />
            </>
          ) : (
            <>
              <div className="bsds-pb-8 bsds-mb-8 border-b border-gray-200">
                <p id={`${popoverId}-title`} className="text-xs font-medium text-gray-600 bsds-text-uppercase bsds-text-center">
                  Seleção Atual
                </p>
              </div>

              <div
                className="bsds-mb-8 bsds-p-8 rounded-md border border-primary-200"
                role="region"
                aria-label="Informações da seleção atual"
              >
                <div className="text-xs font-semibold text-primary-700 bsds-mb-2 break-words" aria-label={displayNameFull}>
                  {displayNameFull}
                </div>
              </div>

              <Button
                color="primary"
                fill="outline"
                onClick={handleBackToSelection}
                children="Trocar seleção"
                className="text-sm"
                aria-label="Trocar seleção e voltar para página de seleção"
              />
            </>
          )}
        </div>
      </SafeBsdsPopover>
    </div>
  );
}

export default CompanyCard;
